import type { AdminSport, AdminSportsbook, SportAffiliateLink, TradedBy } from '../admin/api'
import { useEffect, useMemo, useState } from 'react'
import {
  fetchSportAffiliateLinks,
  fetchSportCarriers,
  LOGO_ACCEPT,
  LOGO_MAX_BYTES,
  updateSportAffiliateLinks,
  updateSportBrand,
  uploadSportLogo,
} from '../admin/api'
import { assetUrl } from '../api'
import OffersPanel from './OffersPanel'

/**
 * /affiliate sport page.
 *
 * Sections: BRAND IDENTITY (editable — logo as an uploaded file or a pasted
 * path, + primary/secondary/tertiary/alternate colors, saved via the
 * update_sport_brand RPC; uploads go to the public brand-logos bucket through
 * POST /admin/sports/:id/logo) → CARRIERS (read-only tiles;
 * carriage is Distribution's call) → AFFILIATE URLS (editable — same
 * full-replace RPC the Distribution editor uses) → OFFERS (interactive POC).
 */

interface Props {
  sport: AdminSport
  sportsbooks: AdminSportsbook[]
  // Re-fetch the catalogs after a brand save so the sidebar + header pick up
  // the new values.
  onSaved: () => void
}

type Mode = 'view' | 'edit'

// The availability map reads these: primary = betting, secondary = DFS,
// tertiary = the combination bucket, alternate = the prediction-market layer.
// Leaving a slot blank is fine — the widget falls back (for alternate, to a
// washed-out tint of primary).
const COLOR_SLOTS = [
  { key: 'brand_color', label: 'PRIMARY', hint: 'betting layer' },
  { key: 'secondary_color', label: 'SECONDARY', hint: 'DFS layer' },
  { key: 'tertiary_color', label: 'TERTIARY', hint: 'combined layer' },
  { key: 'alternate_color', label: 'ALTERNATE', hint: 'prediction layer' },
] as const

const HEX_RE = /^#[0-9a-f]{6}$/i

export default function AffiliateSportPage({ sport, sportsbooks, onSaved }: Props) {
  const [carrierIds, setCarrierIds] = useState<number[]>([])
  // Who trades each carrier (book id → 'us' | 'book'). A book with mixed
  // division rows counts as ASD-traded if any row is ours.
  const [carrierTrades, setCarrierTrades] = useState<Record<number, TradedBy>>({})
  const [links, setLinks] = useState<Record<number, string>>({})
  const [linksBaseline, setLinksBaseline] = useState<Record<number, string>>({})

  // Brand identity edit state.
  const [brandMode, setBrandMode] = useState<Mode>('view')
  const [logoUrl, setLogoUrl] = useState(sport.logo_url ?? '')
  // A picked file wins over the pasted path: it is uploaded on SAVE and its
  // CDN URL becomes logo_url. logoPreview is its object URL for the thumb.
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [colors, setColors] = useState<Record<string, string>>({
    brand_color: sport.brand_color ?? '',
    secondary_color: sport.secondary_color ?? '',
    tertiary_color: sport.tertiary_color ?? '',
    alternate_color: sport.alternate_color ?? '',
  })
  const [brandBusy, setBrandBusy] = useState(false)
  const [brandMsg, setBrandMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  // Affiliate URLs edit state.
  const [urlMode, setUrlMode] = useState<Mode>('view')
  const [urlBusy, setUrlBusy] = useState(false)
  const [urlMsg, setUrlMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  useEffect(() => {
    setCarrierIds([])
    setCarrierTrades({})
    setLinks({})
    setLinksBaseline({})
    setBrandMode('view')
    setUrlMode('view')
    setBrandMsg(null)
    setUrlMsg(null)
    setLogoUrl(sport.logo_url ?? '')
    setLogoFile(null)
    setColors({
      brand_color: sport.brand_color ?? '',
      secondary_color: sport.secondary_color ?? '',
      tertiary_color: sport.tertiary_color ?? '',
      alternate_color: sport.alternate_color ?? '',
    })
    fetchSportCarriers(sport.id)
      .then((rows) => {
        // Dedupe sportsbook ids — division splits (WoO, X Games) would
        // otherwise repeat a book once per division.
        const seen = new Set<number>()
        const ids: number[] = []
        const trades: Record<number, TradedBy> = {}
        for (const r of rows) {
          if (!seen.has(r.sportsbook_id)) {
            seen.add(r.sportsbook_id)
            ids.push(r.sportsbook_id)
          }
          if (r.traded_by === 'us' || trades[r.sportsbook_id] == null) {
            trades[r.sportsbook_id] = r.traded_by
          }
        }
        setCarrierIds(ids)
        setCarrierTrades(trades)
      })
      .catch(() => { /* swallow — non-fatal */ })
    fetchSportAffiliateLinks(sport.id)
      .then((rows) => {
        const map: Record<number, string> = {}
        for (const r of rows) map[r.sportsbook_id] = r.url
        setLinks(map)
        setLinksBaseline(map)
      })
      .catch(() => { /* swallow — non-fatal */ })
  }, [sport.id, sport.logo_url, sport.brand_color, sport.secondary_color, sport.tertiary_color, sport.alternate_color])

  // Object URL for the picked file's live preview; revoked on change/unmount.
  useEffect(() => {
    if (!logoFile) {
      setLogoPreview(null)
      return
    }
    const url = URL.createObjectURL(logoFile)
    setLogoPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [logoFile])

  function pickFile(f: File | null) {
    setBrandMsg(null)
    if (f && f.size > LOGO_MAX_BYTES) {
      setBrandMsg({ kind: 'err', text: `"${f.name}" is ${(f.size / 1024 / 1024).toFixed(2)} MB — the limit is 2 MB` })
      setLogoFile(null)
      return
    }
    setLogoFile(f)
  }

  const carriers = useMemo(
    () => carrierIds
      .map(id => sportsbooks.find(b => b.id === id))
      .filter((b): b is AdminSportsbook => !!b)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [carrierIds, sportsbooks],
  )

  function cancelBrand() {
    setLogoUrl(sport.logo_url ?? '')
    setLogoFile(null)
    setColors({
      brand_color: sport.brand_color ?? '',
      secondary_color: sport.secondary_color ?? '',
      tertiary_color: sport.tertiary_color ?? '',
      alternate_color: sport.alternate_color ?? '',
    })
    setBrandMode('view')
    setBrandMsg(null)
  }

  async function saveBrand() {
    // Normalize: blank = null, and colors must be #rrggbb (the API rejects
    // anything else — catch it here for a friendlier message).
    const normalized: Record<string, string | null> = {}
    for (const slot of COLOR_SLOTS) {
      const v = colors[slot.key].trim()
      if (v && !HEX_RE.test(v)) {
        setBrandMsg({ kind: 'err', text: `${slot.label} must be a #rrggbb hex (got "${v}")` })
        return
      }
      normalized[slot.key] = v || null
    }
    setBrandBusy(true)
    setBrandMsg(null)
    const hadFile = !!logoFile
    try {
      // Upload first so its CDN URL is what the brand write persists. The
      // upload route already stores the URL; the brand PUT then writes the
      // colors with that same URL (two audit rows, same as the sportsbook
      // panel).
      let url: string | null = logoUrl.trim() || null
      if (logoFile) {
        const uploaded = await uploadSportLogo(sport.id, logoFile)
        url = uploaded.logo_url
      }
      await updateSportBrand(sport.id, {
        logo_url: url,
        brand_color: normalized.brand_color ?? null,
        secondary_color: normalized.secondary_color ?? null,
        tertiary_color: normalized.tertiary_color ?? null,
        alternate_color: normalized.alternate_color ?? null,
      })
      setLogoFile(null)
      setLogoUrl(url ?? '')
      setBrandMode('view')
      setBrandMsg({ kind: 'ok', text: hadFile ? 'brand saved — logo uploaded' : 'brand saved' })
      onSaved()
    }
    catch (e) {
      setBrandMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBrandBusy(false)
    }
  }

  function cancelUrls() {
    setLinks(linksBaseline)
    setUrlMode('view')
    setUrlMsg(null)
  }

  async function saveUrls() {
    setUrlBusy(true)
    setUrlMsg(null)
    try {
      // Full-replace, same as the Distribution editor: only carriers with a
      // non-blank URL are sent; clearing an input removes the row.
      const payload: SportAffiliateLink[] = Object.entries(links)
        .map(([id, url]) => ({ sportsbook_id: Number(id), url: url.trim() }))
        .filter(l => l.url !== '')
      await updateSportAffiliateLinks(sport.id, payload)
      const map: Record<number, string> = {}
      for (const l of payload) map[l.sportsbook_id] = l.url
      setLinks(map)
      setLinksBaseline(map)
      setUrlMode('view')
      setUrlMsg({ kind: 'ok', text: 'urls saved' })
    }
    catch (e) {
      setUrlMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setUrlBusy(false)
    }
  }

  const brandReadOnly = brandMode === 'view'
  const urlReadOnly = urlMode === 'view'
  // Edit-mode thumb: the picked file wins, else whatever path is typed.
  const logoEditSrc = logoPreview ?? assetUrl(logoUrl.trim() || null)

  return (
    <section className="ad-editor ad-editor--full">
      <style>{SPORT_PAGE_CSS}</style>
      <header className="ad-editor-head">
        <div className="ad-editor-head-stack">
          {sport.logo_url && (
            <div className="ad-editor-logo ad-editor-logo--sport">
              <img src={assetUrl(sport.logo_url)} alt="" loading="lazy" />
            </div>
          )}
          <h2 className="b-h2">{sport.name}</h2>
        </div>
      </header>

      <div className="ad-editor-section">
        <div className="afx-section-head">
          <h3 className="b-mono">BRAND IDENTITY</h3>
          {brandReadOnly
            ? (
                <button type="button" className="b-btn b-btn-ghost afx-section-btn" onClick={() => { setBrandMode('edit'); setBrandMsg(null) }}>
                  EDIT →
                </button>
              )
            : (
                <span className="afx-section-actions">
                  <button type="button" className="b-btn b-btn-primary afx-section-btn" disabled={brandBusy} onClick={saveBrand}>
                    {brandBusy ? (logoFile ? 'UPLOADING…' : 'SAVING…') : 'SAVE'}
                  </button>
                  <button type="button" className="b-btn b-btn-ghost afx-section-btn" disabled={brandBusy} onClick={cancelBrand}>
                    CANCEL
                  </button>
                </span>
              )}
        </div>
        {brandMsg && (
          <p className={`b-mono afx-msg${brandMsg.kind === 'err' ? ' is-err' : ''}`}>
            {brandMsg.kind === 'err' ? '⚠ ' : '✓ '}
            {brandMsg.text}
          </p>
        )}
        <div className="afo-brand">
          <div className="afo-brand-row">
            <span className="b-mono afo-brand-label">LOGO</span>
            {brandReadOnly
              ? (
                  logoUrl
                    ? (
                        <span className="afo-brand-value">
                          <span className="afx-brand-thumb" aria-hidden="true">
                            <img src={assetUrl(logoUrl)} alt="" loading="lazy" />
                          </span>
                          <span className="b-mono">{logoUrl}</span>
                        </span>
                      )
                    : <span className="b-mono-dim">not set</span>
                )
              : (
                  <span className="afx-logo-edit">
                    <span className="ad-file-row">
                      <span className="afx-brand-thumb afx-brand-thumb--lg" aria-hidden="true">
                        {logoEditSrc
                          ? <img src={logoEditSrc} alt="" />
                          : <span className="b-mono-dim b-tiny">{sport.name.slice(0, 2).toUpperCase()}</span>}
                      </span>
                      <label className="b-btn b-btn-ghost afx-section-btn" style={{ cursor: brandBusy ? 'default' : 'pointer' }}>
                        {logoFile ? 'CHANGE FILE…' : 'UPLOAD FILE…'}
                        <input
                          type="file"
                          className="ad-file-input"
                          accept={LOGO_ACCEPT}
                          disabled={brandBusy}
                          onChange={e => pickFile(e.target.files?.[0] ?? null)}
                        />
                      </label>
                      {logoFile && (
                        <>
                          <span className="ad-file-name">{logoFile.name}</span>
                          <button type="button" className="b-btn-ghost b-tiny" disabled={brandBusy} onClick={() => pickFile(null)}>REMOVE</button>
                        </>
                      )}
                    </span>
                    <input
                      className="ad-input afx-brand-input"
                      value={logoUrl}
                      onChange={e => setLogoUrl(e.target.value)}
                      placeholder="/leagues/<file>.png or https://… — replaced by the upload if a file is chosen"
                      spellCheck={false}
                      disabled={!!logoFile}
                    />
                    <span className="b-mono-dim b-tiny">png / jpeg / webp / svg / gif, ≤ 2 MB — stored in Supabase Storage, served from its CDN</span>
                  </span>
                )}
          </div>
          {COLOR_SLOTS.map((slot) => {
            const value = colors[slot.key]
            const valid = HEX_RE.test(value.trim())
            return (
              <div key={slot.key} className="afo-brand-row">
                <span className="b-mono afo-brand-label">
                  {slot.label}
                  {/* Which availability-map layer this slot paints — otherwise
                      "TERTIARY" tells you nothing about what you are choosing. */}
                  <span className="afo-brand-hint">{slot.hint}</span>
                </span>
                {brandReadOnly
                  ? (
                      value
                        ? (
                            <span className="afo-brand-value">
                              <span className="afo-swatch" style={{ background: value }} aria-hidden="true" />
                              <span className="b-mono">{value}</span>
                            </span>
                          )
                        : <span className="b-mono-dim">not set</span>
                    )
                  : (
                      <span className="afo-brand-value">
                        <input
                          type="color"
                          className="afx-color-pick"
                          value={valid ? value.trim() : '#0db5ff'}
                          onChange={e => setColors(prev => ({ ...prev, [slot.key]: e.target.value }))}
                          aria-label={`${slot.label} color picker`}
                        />
                        <input
                          className="ad-input afx-color-input"
                          value={value}
                          onChange={e => setColors(prev => ({ ...prev, [slot.key]: e.target.value }))}
                          placeholder="#rrggbb (blank = not set)"
                          spellCheck={false}
                        />
                      </span>
                    )}
              </div>
            )
          })}
        </div>
      </div>

      <div className="ad-editor-section">
        <h3 className="b-mono">CARRIERS</h3>
        {carriers.length === 0
          ? <p className="b-mono-dim ad-list-empty">no carriers on record for this sport</p>
          : (
              <div className="afx-carrier-zones">
                {([
                  { key: 'us' as TradedBy, title: 'WE TRADE' },
                  { key: 'book' as TradedBy, title: 'TRADED BY SPORTSBOOK' },
                ]).map(({ key, title }) => {
                  const group = carriers.filter(b => (carrierTrades[b.id] ?? 'us') === key)
                  if (group.length === 0) return null
                  return (
                    <div key={key} className="afx-carrier-zone">
                      <p className="b-mono-dim b-tiny afx-carrier-zone-title">
                        {title} · {group.length}
                      </p>
                      <div className="afx-tiles">
                        {group.map(b => (
                          <span
                            key={b.id}
                            className={`afx-tile${b.logo_fill ? ' is-fill' : ''}`}
                            style={{ background: b.brand_color ?? 'transparent' }}
                            title={`${b.name} — ${key === 'us' ? 'traded by AltSports' : 'traded by the sportsbook'}`}
                          >
                            {b.logo_url
                              ? <img src={assetUrl(b.logo_url)} alt={b.name} loading="lazy" />
                              : <span className="afx-tile-text">{b.name.slice(0, 2).toUpperCase()}</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
      </div>

      <div className="ad-editor-section">
        <div className="afx-section-head">
          <h3 className="b-mono">AFFILIATE URLS</h3>
          {carriers.length > 0 && (
            urlReadOnly
              ? (
                  <button type="button" className="b-btn b-btn-ghost afx-section-btn" onClick={() => { setUrlMode('edit'); setUrlMsg(null) }}>
                    EDIT →
                  </button>
                )
              : (
                  <span className="afx-section-actions">
                    <button type="button" className="b-btn b-btn-primary afx-section-btn" disabled={urlBusy} onClick={saveUrls}>
                      {urlBusy ? 'SAVING…' : 'SAVE'}
                    </button>
                    <button type="button" className="b-btn b-btn-ghost afx-section-btn" disabled={urlBusy} onClick={cancelUrls}>
                      CANCEL
                    </button>
                  </span>
                )
          )}
        </div>
        {urlMsg && (
          <p className={`b-mono afx-msg${urlMsg.kind === 'err' ? ' is-err' : ''}`}>
            {urlMsg.kind === 'err' ? '⚠ ' : '✓ '}
            {urlMsg.text}
          </p>
        )}
        {carriers.length === 0
          ? <p className="b-mono-dim ad-list-empty">no carriers, no URLs</p>
          : (
              <div className="ad-affiliate-list">
                {carriers.map((b) => {
                  const hasBrand = b.brand_color != null
                  const hasLogo = b.logo_url != null
                  return (
                    <label key={b.id} className="ad-affiliate-row">
                      <span className="ad-affiliate-label b-mono">
                        {(hasBrand || hasLogo) && (
                          <span
                            className={`ad-affiliate-logo${b.logo_fill ? ' is-fill' : ''}`}
                            style={{ background: b.brand_color ?? 'transparent' }}
                            aria-hidden="true"
                          >
                            {hasLogo
                              ? <img src={assetUrl(b.logo_url!)} alt="" loading="lazy" />
                              : <span className="ad-affiliate-logo-text">{b.name.slice(0, 2).toUpperCase()}</span>}
                          </span>
                        )}
                        {b.name}
                      </span>
                      <input
                        type="url"
                        value={links[b.id] ?? ''}
                        onChange={e => setLinks(prev => ({ ...prev, [b.id]: e.target.value }))}
                        disabled={urlReadOnly}
                        placeholder="https://…"
                        className="ad-input ad-affiliate-input"
                      />
                    </label>
                  )
                })}
              </div>
            )}
      </div>

      <OffersPanel
        key={sport.id}
        sportId={sport.id}
        books={sportsbooks}
        carrierIds={carrierIds}
      />
    </section>
  )
}

const SPORT_PAGE_CSS = `
.afx-carrier-zones { display: flex; flex-direction: column; gap: 14px; }
.afx-carrier-zone-title { margin: 0 0 6px; letter-spacing: .08em; }
.afx-section-head { display: flex; align-items: center; gap: 16px; }
.afx-section-head h3 { margin: 0; }
.afx-section-actions { display: inline-flex; gap: 8px; }
.afx-section-btn { font-size: 11px; padding: 3px 10px; cursor: pointer; }
.afx-msg { font-size: 12px; margin: 6px 0 0; }
.afx-msg.is-err { color: #b91c1c; }
.brutalist-root.is-dark .afx-msg.is-err { color: #f87171; }
.afo-brand { display: flex; flex-direction: column; gap: 6px; margin-top: 8px; }
.afo-brand-row {
  display: grid;
  grid-template-columns: minmax(120px, 160px) 1fr;
  gap: 12px;
  align-items: center;
  font-size: 12px;
}
.afo-brand-label { opacity: 0.7; }

.afo-brand-hint {
  display: block;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: .04em;
  text-transform: none;
  opacity: .6;
}
.afo-brand-value { display: inline-flex; align-items: center; gap: 8px; word-break: break-all; }
.afo-swatch {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid rgba(128, 128, 128, 0.4);
  flex: 0 0 18px;
}
.afx-brand-thumb {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid rgba(128, 128, 128, 0.3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
  flex: 0 0 32px;
}
.afx-brand-thumb img { max-width: 100%; max-height: 100%; object-fit: contain; }
.afx-brand-thumb--lg { width: 48px; height: 48px; flex: 0 0 48px; padding: 4px; }
.afx-brand-input { max-width: 420px; }
.afx-logo-edit { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.afx-logo-edit .afx-brand-input { max-width: 640px; width: 100%; }
.afx-color-pick {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid rgba(128, 128, 128, 0.4);
  border-radius: 4px;
  background: none;
  cursor: pointer;
}
.afx-color-input { max-width: 200px; }
.afx-tiles { display: flex; flex-wrap: wrap; gap: 8px; }
.afx-tile {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.afx-tile.is-fill { padding: 0; }
.afx-tile img { max-width: 100%; max-height: 100%; object-fit: contain; }
.afx-tile.is-fill img { width: 100%; height: 100%; object-fit: cover; }
.afx-tile-text { color: #fff; font-weight: 700; font-size: 14px; }
`
